import { Component, Input, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { catchError, finalize } from "rxjs";
import { ToastrService } from "ngx-toastr";

// NG-ZORRO Imports
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzValidateStatus } from 'ng-zorro-antd/core/types';
import { NzAlertModule } from 'ng-zorro-antd/alert'; // Added for the warning message style

import { UserDto } from "../../dto";
import { HttpUserService } from "../../services/user/http-user.service";
import { NzCheckboxModule } from "ng-zorro-antd/checkbox";

export const passwordMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const password = control.get('newPassword')?.value;
    const confirm = control.get('confirmPassword')?.value;
    return password && confirm && password !== confirm ? { passwordMismatch: true } : null;
};

@Component({
    selector: 'app-reset-password-modal',
    standalone: true,
    imports: [
        CommonModule, 
        ReactiveFormsModule,
        NzModalModule,
        NzButtonModule,
        NzFormModule,
        NzInputModule,
        NzAlertModule,
        NzCheckboxModule
    ],
    templateUrl: './reset-password-modal.component.html',
    styleUrls: ['./reset-password-modal.component.scss']
})
export class ResetPasswordModalComponent implements OnInit {
    @Input() modalId = 'resetPasswordModal';
    user!: UserDto | undefined;

    form!: FormGroup;
    
    // UI State
    isVisible = false;
    isConfirmLoading = false;

    serverErrors: string[] = [];

    constructor(
        private fb: FormBuilder, 
        private httpService: HttpUserService, 
        private toastr: ToastrService
        // Removed SpinnerService
    ) {}

    ngOnInit() {
        this.form = this.fb.group({
            newPassword: ['', Validators.required],
            confirmPassword: ['', Validators.required],
            forceReset: [false]
        }, { validators: passwordMatchValidator });

        this.form.get('newPassword')?.valueChanges.subscribe(() => {
            this.form.get('confirmPassword')?.updateValueAndValidity({ emitEvent: false });
        });
    }

    get confirmPasswordStatus(): NzValidateStatus {
        if (this.form.get('confirmPassword')?.invalid && this.form.get('confirmPassword')?.touched) {
            return 'error';
        }
        if (this.form.hasError('passwordMismatch') && this.form.get('confirmPassword')?.touched) {
            return 'error';
        }
        return '';
    }

    open(user: UserDto) {
        this.user = user;
        this.serverErrors = [];
        this.form.reset({
            newPassword: '',
            confirmPassword: '',
            forceReset: false
        });
        this.isVisible = true;
    }

    handleCancel(): void {
        this.isVisible = false;
    }

    save() {
        this.serverErrors = [];
        if (!this.form.valid) {
            Object.values(this.form.controls).forEach(control => {
                if (control.invalid) {
                    control.markAsDirty();
                    control.updateValueAndValidity({ onlySelf: true });
                }
            });
            this.form.get('confirmPassword')?.markAsTouched();
            return;
        }

        this.isConfirmLoading = true;

        const payload: UserDto = {
            ...this.user, 
            password: this.form.get('newPassword')?.value, 
            forceChangePassword: this.form.get('forceReset')?.value
        };
        
        this.httpService.resetPassword(payload)
            .pipe(
                catchError(error => {
                    console.log('Error setting user password', error);
                    this.toastr.error('Failed to set password: ' + (error.error ? error.error.error : error.message));
                    throw error;
                }),
                finalize(() => this.isConfirmLoading = false)
            )
            .subscribe(data => {
                if (data.success) {
                    this.toastr.success("Password set successfully");
                    this.isVisible = false;
                } else {
                    this.form.reset({
                        newPassword: '',
                        confirmPassword: '',
                        forceReset: this.form.get('forceReset')?.value // preserve the checkbox state
                    });
                    console.log(data);
                    if (data.errors && data.errors.length > 0) {
                        this.serverErrors = data.errors;
                        this.toastr.error('Password does not meet policy requirements.');
                    } else {
                        this.serverErrors = [data.error];
                        this.toastr.error('Failed to set password. Reason: ' + data.error);
                    }
                }
            });
    }
}